import {
  Datatable,
  DatatableBody,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
} from '@monksflow/monks-ui';
import type { TemplateItem } from '@/features/templates-config';
import { TABLE_COL_WIDTH, TABLE_NAME_COL_NO_SIDEBAR } from '@/shared/constants';
import { SortableHead, type SortDirection } from '@/shared/ui/data-table';
import type { EmailTemplateSortColumn } from '../../sortTemplates';
import { useTranslation } from 'react-i18next';
import { EmailTemplatesTableRow } from '../EmailTemplatesTableRow';
import { EmailTemplatesTableSkeletonRow } from './EmailTemplatesTableSkeletonRow';

interface EmailTemplatesTableProps {
  templates: TemplateItem[];
  isLoading: boolean;
  sortBy: EmailTemplateSortColumn;
  sortDirection: SortDirection;
  onSort: (column: EmailTemplateSortColumn) => void;
  onRename: (slug: string, templateName: string) => void;
  onDelete: (slug: string) => void;
}

const SKELETON_ROWS = Array.from({ length: 5 }, (_, index) => index);

/**
 * Pure presentation; empty / error / no-results states belong to the orchestrator.
 *
 * `aria-busy` is a composite gate (`isLoading && templates.length === 0`) so a background refetch
 * doesn't announce a busy state over content that is already on screen.
 */
export function EmailTemplatesTable({
  templates,
  isLoading,
  sortBy,
  sortDirection,
  onSort,
  onRename,
  onDelete,
}: EmailTemplatesTableProps) {
  const { t } = useTranslation('knowledge');

  const isBusy = isLoading && templates.length === 0;

  // Header divider = 1px to match the app's other list tables; the `Datatable` primitive defaults
  // `DatatableHead` to `border-b-2`.
  return (
    <Datatable aria-busy={isBusy || undefined} className="[&_thead_th]:border-b">
      <DatatableHeader>
        <DatatableRow>
          <SortableHead
            column="templateName"
            label={t('knowledgeHub.emailTemplatesTable.name')}
            className={TABLE_NAME_COL_NO_SIDEBAR}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHead
            column="updatedAt"
            label={t('knowledgeHub.emailTemplatesTable.dateModified')}
            className={TABLE_COL_WIDTH.dateText}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          {/* Balancing spacer — pads the data columns to the shared 760px no-sidebar region so the
              Name column lines up with the other list tables. 596px + 112px date + 52px actions =
              760px. See table-layout.constants; don't drop it without re-balancing. */}
          <DatatableHead scope="col" aria-hidden="true" className="w-149" />
          <DatatableHead scope="col" className={TABLE_COL_WIDTH.actions}>
            <span className="sr-only">{t('knowledgeHub.emailTemplatesTable.actions')}</span>
          </DatatableHead>
        </DatatableRow>
      </DatatableHeader>
      <DatatableBody>
        {isBusy
          ? SKELETON_ROWS.map((index) => <EmailTemplatesTableSkeletonRow key={index} />)
          : templates.map((template) => (
              <EmailTemplatesTableRow
                key={template.slug}
                template={template}
                onRename={onRename}
                onDelete={onDelete}
              />
            ))}
      </DatatableBody>
    </Datatable>
  );
}
